import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import IconRing from "../subcomponents/IconRing";
import DisabledPlus from "../../../../../../assets/icons/Icon - Add - Disabled.svg";
import CompleteTaskIcon from "../../../../../../assets/icons/Icon-Complete-Task-Green-20.svg";
import CancelTaskIcon from "../../../../../../assets/icons/Icon-Cancel-Task-Red-20.svg";
import RequestReviewIcon from "../../../../../../assets/icons/Icon-Request-Review-Blue-20.svg";
import ApproveTaskIcon from "../../../../../../assets/icons/Icon-Approve-Task-Green-20.svg";
import DeclineTaskIcon from "../../../../../../assets/icons/Icon-Decline-Task-Red-20.svg";

// Regex rules: "task"/"tasks" is optional, case-insensitive, ignores surrounding spaces.
const ACTION_ICON_RULES = [
  { re: /^\s*complete(?:\s+tasks?)?\s*$/i, icon: CompleteTaskIcon, alt: "Complete Task" },
  { re: /^\s*cancel(?:\s+tasks?)?\s*$/i, icon: CancelTaskIcon, alt: "Cancel Task" },
  { re: /^\s*request\s*review(?:\s+tasks?)?\s*$/i, icon: RequestReviewIcon, alt: "Request Review" },
  { re: /^\s*approve(?:\s+tasks?)?\s*$/i, icon: ApproveTaskIcon, alt: "Approve Task" },
  // Also match "Reject" as a synonym for decline
  { re: /^\s*(?:decline|reject)(?:\s+tasks?)?\s*$/i, icon: DeclineTaskIcon, alt: "Decline Task" },
];

const getIconForAction = (name) => {
  if (!name || typeof name !== "string") {
    return { src: DisabledPlus, alt: "Base Action" };
  }
  const rule = ACTION_ICON_RULES.find(({ re }) => re.test(name));
  return rule ? { src: rule.icon, alt: rule.alt } : { src: DisabledPlus, alt: "Base Action" };
};

const BaseActionRow = ({ field, index, onHide, onOpen }) => {
  const hideDisabled = !!(field?.AutomaticTriggers && String(field.AutomaticTriggers).trim() !== "");

  const { src: actionIcon, alt: actionAlt } = useMemo(
    () => getIconForAction(field?.Name),
    [field?.Name]
  );

  return (
    <div className="individual-workflow" id={`workflowstatusBase_${index}`}>
      <div className="individual-workflow-status">
        <span className="status-index">{index + 1}</span>
        <IconRing src={actionIcon} alt={actionAlt} iconSize={16} padding={10} />
        <div className="field-container-base-status pl-2">
          {field?.Name}
          <OverlayTrigger
            placement="top"
            delay={{ show: 150, hide: 150 }}
            overlay={
              <Tooltip id={`hide-tooltip-${field?.Id ?? index}`}>
                {hideDisabled
                  ? "This Task Action is set to be automatically triggered and can’t be hidden."
                  : "Click to Hide"}
              </Tooltip>
            }
          >
            <Button
              className={`btn-icon1 hide-eye-icon ${hideDisabled ? "hide-eye-icon-disabled" : ""}`}
              onClick={() => {
                if (!hideDisabled) onHide(field.Id, field?.IsHidden);
              }}
              type="button"
              aria-label={hideDisabled ? "Auto-triggered action (hide disabled)" : "Hide action"}
            />
          </OverlayTrigger>
        </div>

        <OverlayTrigger
          placement="top"
          delay={{ show: 150, hide: 150 }}
          overlay={
            <Tooltip id={`lock-tooltip-${field?.Id ?? index}`}>
              TRIYO created this Base Task Action. You can’t edit its name and properties.
            </Tooltip>
          }
        >
          <Button className="btn-icon1 lock-closed-icon" type="button" aria-label="Locked" />
        </OverlayTrigger>
      </div>
    </div>
  );
};

BaseActionRow.propTypes = {
  field: PropTypes.shape({
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Name: PropTypes.string.isRequired,
    AutomaticTriggers: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    IsHidden: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onHide: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default BaseActionRow;
